// client/src/coms/personalisation/ThemeSettings.js
// Settings → Appearance → Theme: built-in themes, accent, saved custom themes (apply, edit, duplicate, rename,
// delete, export), import, and the custom theme editor with live preview and a readability check.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePersonalisation } from './PersonalisationProvider';
import { Row, Section, Segmented, Slider } from './settingsControls';
import { BUILT_IN_THEMES, THEME_MODE_OPTIONS, exportThemeFile, parseThemeFile, readableOn, resolveTheme, themeFromBuiltIn, themeAccessibility } from './themeEngine';
import { DEFAULT_PREFS, THEME_COLOR_KEYS, sanitizeTheme, validateThemeStrict } from './prefsSchema';

const pct = (v) => `${Math.round(v * 100)}%`;

const COLOR_LABELS = {
  accent: 'Accent',
  background: 'App background',
  surface: 'Chat surface',
  panel: 'Panels',
  sidebar: 'Channel sidebar',
  text: 'Text',
  textMuted: 'Secondary text',
  border: 'Borders',
  messageBg: 'Message area',
  link: 'Links',
  hover: 'Hover',
  selected: 'Selected',
};

export function ThemeSwatch({ theme, size = 'md' }) {
  const t = resolveTheme(theme);
  const c = t.colors;
  return (
    <span className={`vx-swatch vx-swatch-${size}`} aria-hidden="true" style={{ background: c.background }}>
      <span style={{ background: c.sidebar || c.panel }} />
      <span style={{ background: c.surface }}>
        <i style={{ background: c.text }} />
        <i style={{ background: c.textMuted }} />
        <b style={{ background: c.accent }} />
      </span>
    </span>
  );
}

function download(filename, text) {
  try {
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch {}
}

function safeFileName(name) {
  return `${String(name || 'theme').replace(/[^a-z0-9-_ ]/gi, '').trim().replace(/\s+/g, '-').toLowerCase() || 'theme'}.voxemera-theme.json`;
}

function AccessibilityReport({ theme }) {
  const report = useMemo(() => themeAccessibility(theme), [theme]);
  return (
    <div className={`vx-a11y ${report.ok ? 'is-ok' : 'is-bad'}`} role="status" aria-live="polite" data-testid="theme-a11y">
      {report.ok ? (
        <span>✓ Text is readable on every surface.</span>
      ) : (
        <>
          <span>Some text may be hard to read:</span>
          <ul>
            {report.failures.map((f) => (
              <li key={f.pair}>
                {f.pair}: contrast {f.ratio}:1 (needs {f.min}:1)
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// Live preview card (independent of the app around it).
function PreviewCard({ theme }) {
  const t = resolveTheme(theme);
  const c = t.colors;
  return (
    <div className="vx-preview" style={{ background: c.background, color: c.text }} aria-label="Theme preview" role="img">
      <div className="vx-preview-side" style={{ background: c.sidebar || c.panel, borderColor: c.border || 'transparent' }}>
        <div style={{ color: c.textMuted }}># general</div>
        <div style={{ background: c.selected || 'rgba(127,127,127,0.25)', color: c.text }}># design</div>
        <div style={{ color: c.textMuted }}># random</div>
      </div>
      <div className="vx-preview-main" style={{ background: c.messageBg || c.surface }}>
        <div>
          <b>Ada</b> <span style={{ color: c.textMuted }}>today</span>
        </div>
        <div>
          Here is the <span style={{ color: c.link, textDecoration: 'underline' }}>link</span> we discussed.
        </div>
        <div className="vx-preview-btn" style={{ background: c.accent, color: readableOn(c.accent) }}>
          Send
        </div>
      </div>
    </div>
  );
}

export function ThemeEditor({ initial, onCancel, onSave, saveLabel = 'Save theme', title = 'Custom theme' }) {
  const { setThemePreview } = usePersonalisation();
  const [draft, setDraft] = useState(() => sanitizeTheme(initial || themeFromBuiltIn('dark', 'My theme')));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const firstRef = useRef(null);

  // The whole app previews the theme while the editor is open; Cancel puts the saved theme back.
  useEffect(() => {
    setThemePreview(draft);
  }, [draft, setThemePreview]);
  useEffect(() => () => setThemePreview(null), [setThemePreview]);
  useEffect(() => {
    if (firstRef.current) firstRef.current.focus();
  }, []);

  const base = resolveTheme(draft.base === 'light' ? 'light' : 'dark').colors;
  const set = (patch) => setDraft((d) => sanitizeTheme({ ...d, ...patch }));
  const setColor = (key, value) => set({ colors: { ...draft.colors, [key]: value } });
  const clearColor = (key) => {
    const next = { ...draft.colors };
    delete next[key];
    set({ colors: next });
  };

  const submit = async (asNew) => {
    const checked = validateThemeStrict(draft);
    if (!checked.ok) {
      setError(checked.errors.join(' '));
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onSave(checked.theme, { asNew });
    } catch (e) {
      setError((e && e.data && e.data.errors && e.data.errors.join(' ')) || (e && e.message) || 'Could not save the theme.');
      setBusy(false);
    }
  };

  const grad = draft.gradient;
  return (
    <div className="vx-editor" role="region" aria-label={title} data-testid="theme-editor">
      <div className="vx-editor-head">
        <h4>{title}</h4>
        <span className="vx-set-hint">Changes preview across Voxemera until you save or cancel.</span>
      </div>
      <div className="vx-editor-grid">
        <div className="vx-editor-fields">
          <label className="vx-field">
            <span>Name</span>
            <input ref={firstRef} type="text" maxLength={40} value={draft.name} onChange={(e) => set({ name: e.target.value })} data-testid="theme-name" />
          </label>
          <div className="vx-field">
            <span>Based on</span>
            <Segmented
              label="Based on"
              value={draft.base}
              options={[
                { value: 'dark', label: 'Dark' },
                { value: 'light', label: 'Light' },
              ]}
              onChange={(v) => set({ base: v })}
            />
          </div>
          <div className="vx-field">
            <span>Contrast</span>
            <Segmented
              label="Contrast"
              value={draft.contrast}
              options={[
                { value: 'normal', label: 'Normal' },
                { value: 'high', label: 'High (stronger borders)' },
              ]}
              onChange={(v) => set({ contrast: v })}
            />
          </div>
          <div className="vx-colors">
            {THEME_COLOR_KEYS.map((key) => {
              const own = draft.colors[key];
              const shown = own || base[key] || (key === 'sidebar' ? base.panel : key === 'messageBg' ? base.surface : key === 'border' ? '#3b3c40' : key === 'hover' || key === 'selected' ? '#404249' : '#888888');
              return (
                <div key={key} className={`vx-color${own ? ' is-set' : ''}`}>
                  <label>
                    <input type="color" value={shown} onChange={(e) => setColor(key, e.target.value)} aria-label={`${COLOR_LABELS[key]} colour`} data-testid={`theme-color-${key}`} />
                    <span>{COLOR_LABELS[key]}</span>
                  </label>
                  {own ? (
                    <button type="button" className="vx-link-btn" onClick={() => clearColor(key)} aria-label={`Use the default ${COLOR_LABELS[key]} colour`}>
                      default
                    </button>
                  ) : (
                    <span className="vx-set-hint">default</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="vx-field">
            <span>Background</span>
            <Segmented
              label="Background style"
              value={grad ? grad.type : 'none'}
              options={[
                { value: 'none', label: 'Solid' },
                { value: 'linear', label: 'Linear gradient' },
                { value: 'radial', label: 'Radial gradient' },
              ]}
              onChange={(v) =>
                set({ gradient: v === 'none' ? null : { type: v, angle: (grad && grad.angle) || 135, stops: (grad && grad.stops) || [draft.colors.background || base.background, draft.colors.accent || base.accent] } })
              }
            />
            {grad ? (
              <div className="vx-grad">
                {grad.stops.map((s, i) => (
                  <input
                    key={i}
                    type="color"
                    value={s}
                    aria-label={`Gradient colour ${i + 1}`}
                    onChange={(e) => set({ gradient: { ...grad, stops: grad.stops.map((x, j) => (j === i ? e.target.value : x)) } })}
                  />
                ))}
                {grad.stops.length < 3 ? (
                  <button type="button" className="vx-btn small" onClick={() => set({ gradient: { ...grad, stops: [...grad.stops, grad.stops[grad.stops.length - 1]] } })}>
                    + colour
                  </button>
                ) : (
                  <button type="button" className="vx-btn small" onClick={() => set({ gradient: { ...grad, stops: grad.stops.slice(0, 2) } })}>
                    − colour
                  </button>
                )}
                {grad.type === 'linear' ? (
                  <label className="vx-inline">
                    Angle
                    <input type="range" min={0} max={360} step={5} value={grad.angle} onChange={(e) => set({ gradient: { ...grad, angle: Number(e.target.value) } })} aria-label="Gradient angle" />
                    <span>{grad.angle}°</span>
                  </label>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="vx-editor-side">
          <PreviewCard theme={draft} />
          <AccessibilityReport theme={draft} />
        </div>
      </div>
      {error ? (
        <div className="vx-acct-error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="vx-editor-actions">
        <button type="button" className="vx-btn" onClick={() => set(themeFromBuiltIn(draft.base === 'light' ? 'light' : 'dark', draft.name))} title="Start again from the base theme">
          Reset
        </button>
        <span style={{ flex: 1 }} />
        <button type="button" className="vx-btn" onClick={onCancel} data-testid="theme-cancel">
          Cancel
        </button>
        {initial && initial.__presetId ? (
          <button type="button" className="vx-btn" disabled={busy} onClick={() => submit(true)} data-testid="theme-save-new">
            Save as new
          </button>
        ) : null}
        <button type="button" className="vx-btn primary" disabled={busy} onClick={() => submit(false)} data-testid="theme-save">
          {saveLabel}
        </button>
      </div>
    </div>
  );
}

export default function ThemeSettings() {
  const pz = usePersonalisation();
  const { globalPrefs, themes, setThemeValue, updateGlobal } = pz;
  const theme = globalPrefs.theme;
  const layout = globalPrefs.layout;
  const DT = DEFAULT_PREFS.theme;
  const DL = DEFAULT_PREFS.layout;
  const themeIsDefault = theme.mode === DT.mode && !theme.accent && theme.reduceMotion === DT.reduceMotion && theme.focusRing === DT.focusRing && theme.serverThemes === DT.serverThemes;
  // Built-in look and accessibility back to defaults; saved custom themes are kept (only no longer applied).
  const restoreDefaults = () =>
    updateGlobal((p) => ({
      ...p,
      theme: { ...p.theme, mode: DT.mode, accent: null, reduceMotion: DT.reduceMotion, focusRing: DT.focusRing, serverThemes: DT.serverThemes },
      layout: { ...p.layout, messageScale: DL.messageScale, uiScale: DL.uiScale },
    }));
  const [editing, setEditing] = useState(null); // { initial, presetId }
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [confirmDelete, setConfirmDelete] = useState(null); // preset id
  const [message, setMessage] = useState('');
  const [importError, setImportError] = useState([]);
  const fileRef = useRef(null);

  const chooseMode = (mode) => {
    if (mode === 'custom') {
      if (themes.length) pz.applyThemePreset(themes.find((t) => t.id === theme.customId) || themes[0]);
      else setEditing({ initial: themeFromBuiltIn('dark', 'My theme') });
      return;
    }
    updateGlobal((p) => ({ ...p, theme: { ...p.theme, mode } }));
  };

  const saveEdited = async (t, { asNew }) => {
    let preset;
    if (editing.presetId && !asNew) preset = await pz.updateThemePreset(editing.presetId, { theme: t, name: t.name });
    else preset = await pz.createThemePreset(t.name, t);
    if (preset) pz.applyThemePreset(preset);
    setEditing(null);
    setMessage(`Saved “${t.name}” and applied it.`);
  };

  const onImportFile = async (file) => {
    setImportError([]);
    if (!file) return;
    if (file.size > 8192) {
      setImportError(['That file is too large to be a theme.']);
      return;
    }
    const text = await file.text();
    const res = parseThemeFile(text);
    if (!res.ok) {
      setImportError(res.errors);
      return;
    }
    try {
      const preset = await pz.createThemePreset(res.theme.name, res.theme, { source: 'import' });
      setMessage(`Imported “${res.theme.name}”.${res.accessibility && !res.accessibility.ok ? ' Some text may be hard to read with it.' : ''}`);
      if (preset) pz.applyThemePreset(preset);
    } catch (e) {
      setImportError((e && e.data && e.data.errors) || [e.message || 'Could not import the theme.']);
    }
  };

  return (
    <Section
      id="vx-theme"
      title="Themes & Accessibility"
      description="Colours, text size and motion. Saved to your account, so every device you sign in on looks the same. Changes apply immediately."
      actions={
        !themeIsDefault || layout.messageScale !== DL.messageScale || layout.uiScale !== DL.uiScale ? (
          <button type="button" className="vx-btn small" onClick={restoreDefaults} data-testid="theme-restore-defaults">
            Restore defaults
          </button>
        ) : null
      }
    >
      {message ? (
        <div className="vx-set-message" role="status">
          {message}
        </div>
      ) : null}
      <div id="vx-setting-theme" data-setting="theme" />
      <div className="vx-theme-grid" role="radiogroup" aria-label="Theme" data-testid="theme-modes">
        {THEME_MODE_OPTIONS.filter((o) => o.value !== 'custom').map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={theme.mode === o.value}
            className={`vx-theme-card${theme.mode === o.value ? ' is-on' : ''}`}
            onClick={() => chooseMode(o.value)}
            data-testid={`theme-mode-${o.value}`}
          >
            {o.value === 'system' ? (
              <span className="vx-swatch-pair">
                <ThemeSwatch theme="dark" size="sm" />
                <ThemeSwatch theme="light" size="sm" />
              </span>
            ) : (
              <ThemeSwatch theme={o.value} />
            )}
            <span>{o.label}</span>
          </button>
        ))}
      </div>

      <Row
        label="Accent colour"
        hint="Buttons, selections and highlights in the built-in themes. Custom themes set their own."
        settingId="accent"
        scope="account"
        onReset={() => setThemeValue('accent', null)}
        isDefault={!theme.accent}
        resetLabel="Use default"
      >
        <div className="vx-inline">
          <input
            type="color"
            value={theme.accent || (BUILT_IN_THEMES[theme.mode] || BUILT_IN_THEMES.dark).colors.accent}
            onChange={(e) => setThemeValue('accent', e.target.value)}
            aria-label="Accent colour"
            data-testid="theme-accent"
            disabled={theme.mode === 'custom'}
          />
        </div>
      </Row>

      <h4 className="vx-set-subtitle">Readability</h4>
      <Row label="Message text size" hint="Size of messages in conversations." settingId="text-size" scope="account" onReset={() => pz.setLayoutValue('messageScale', DL.messageScale)} isDefault={layout.messageScale === DL.messageScale}>
        <Slider label="Message text size" value={layout.messageScale} min={0.8} max={1.5} step={0.05} onChange={(v) => pz.setLayoutValue('messageScale', v)} format={pct} testId="layout-message-scale" />
      </Row>
      <Row
        label="Interface scale"
        hint="Makes the whole interface larger or smaller. Not used on phone-sized screens, which follow your device’s text size. Canvas, Activities and Maps stay at 100% so drawing and pointing stay precise."
        settingId="ui-scale"
        scope="account"
        onReset={() => pz.setLayoutValue('uiScale', DL.uiScale)}
        isDefault={layout.uiScale === DL.uiScale}
      >
        <Slider label="Interface scale" value={layout.uiScale} min={0.8} max={1.3} step={0.05} onChange={(v) => pz.setLayoutValue('uiScale', v)} format={pct} testId="layout-ui-scale" />
      </Row>
      <Row
        label="Focus outlines"
        hint="The outline that shows where you are when moving around with the keyboard. Stronger makes it thicker with a contrasting edge."
        settingId="focus-ring"
        scope="account"
        onReset={() => setThemeValue('focusRing', DT.focusRing)}
        isDefault={theme.focusRing === DT.focusRing}
      >
        <Segmented
          label="Focus outlines"
          value={theme.focusRing}
          options={[
            { value: 'standard', label: 'Standard' },
            { value: 'strong', label: 'Stronger' },
          ]}
          onChange={(v) => setThemeValue('focusRing', v)}
          testId="theme-focus-ring"
        />
      </Row>

      <Row
        label="Reduce motion"
        hint={`Turns off animations and smooth scrolling. Speaking animations become still badges (you can change that under Speaking Animations).${pz.systemReducedMotion ? ' Your device currently asks for reduced motion.' : ''}`}
        settingId="reduce-motion"
        scope="account"
        onReset={() => setThemeValue('reduceMotion', DT.reduceMotion)}
        isDefault={theme.reduceMotion === DT.reduceMotion}
      >
        <Segmented
          label="Reduce motion"
          value={theme.reduceMotion}
          options={[
            { value: 'system', label: 'Match system' },
            { value: 'on', label: 'On' },
            { value: 'off', label: 'Off' },
          ]}
          onChange={(v) => setThemeValue('reduceMotion', v)}
        />
      </Row>

      <Row label="Server theme suggestions" hint="Some servers suggest a theme. It is never applied unless you choose it." settingId="server-themes" scope="account">
        <Segmented
          label="Server themes"
          value={theme.serverThemes}
          options={[
            { value: 'ask', label: 'Offer them' },
            { value: 'never', label: 'Never ask' },
          ]}
          onChange={(v) => setThemeValue('serverThemes', v)}
        />
      </Row>

      <div className="vx-set-subhead" id="vx-setting-custom-themes" data-setting="custom-themes">
        <h4>Your themes</h4>
        <div className="vx-inline">
          <button type="button" className="vx-btn small primary" onClick={() => setEditing({ initial: themeFromBuiltIn(theme.mode === 'light' ? 'light' : 'dark', 'My theme') })} data-testid="theme-create">
            Create theme
          </button>
          <button type="button" className="vx-btn small" onClick={() => fileRef.current && fileRef.current.click()} data-testid="theme-import">
            Import…
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            hidden
            data-testid="theme-import-file"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              e.target.value = '';
              onImportFile(f);
            }}
          />
        </div>
      </div>
      {importError.length ? (
        <div className="vx-acct-error" role="alert" data-testid="theme-import-error">
          That theme could not be imported:
          <ul>
            {importError.slice(0, 6).map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {editing ? (
        <ThemeEditor
          initial={editing.presetId ? { ...editing.initial, __presetId: editing.presetId } : editing.initial}
          title={editing.presetId ? `Edit “${editing.initial.name}”` : 'New theme'}
          saveLabel={editing.presetId ? 'Save changes' : 'Save and apply'}
          onCancel={() => setEditing(null)}
          onSave={saveEdited}
        />
      ) : null}

      <ul className="vx-presets" aria-label="Saved themes" data-testid="theme-presets">
        {!themes.length ? <li className="vx-set-hint">No saved themes yet.</li> : null}
        {themes.map((t) => {
          const active = theme.mode === 'custom' && theme.customId === t.id;
          return (
            <li key={t.id} className={`vx-preset${active ? ' is-on' : ''}`} data-testid="theme-preset">
              <ThemeSwatch theme={t.theme} size="sm" />
              {renaming && renaming.id === t.id ? (
                <form
                  className="vx-inline"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    await pz.updateThemePreset(t.id, { name: renaming.name });
                    setRenaming(null);
                  }}
                >
                  <input autoFocus type="text" maxLength={40} value={renaming.name} onChange={(e) => setRenaming({ id: t.id, name: e.target.value })} aria-label="Theme name" />
                  <button type="submit" className="vx-btn small">
                    Save
                  </button>
                  <button type="button" className="vx-btn small" onClick={() => setRenaming(null)}>
                    Cancel
                  </button>
                </form>
              ) : (
                <span className="vx-preset-name">
                  {t.name}
                  {active ? <span className="vx-acct-badge on">In use</span> : null}
                </span>
              )}
              <span className="vx-preset-actions">
                {!active ? (
                  <button type="button" className="vx-btn small primary" onClick={() => pz.applyThemePreset(t)} data-testid="theme-preset-apply">
                    Apply
                  </button>
                ) : null}
                <button type="button" className="vx-btn small" onClick={() => setEditing({ initial: t.theme, presetId: t.id })} aria-label={`Edit ${t.name}`}>
                  Edit
                </button>
                <button type="button" className="vx-btn small" onClick={() => pz.duplicateThemePreset(t.id)} aria-label={`Duplicate ${t.name}`}>
                  Duplicate
                </button>
                <button type="button" className="vx-btn small" onClick={() => setRenaming({ id: t.id, name: t.name })} aria-label={`Rename ${t.name}`}>
                  Rename
                </button>
                <button type="button" className="vx-btn small" onClick={() => download(safeFileName(t.name), exportThemeFile(t.theme))} aria-label={`Export ${t.name}`} data-testid="theme-preset-export">
                  Export
                </button>
                {confirmDelete === t.id ? (
                  <>
                    <button
                      type="button"
                      className="vx-btn small danger"
                      data-testid="theme-preset-delete-confirm"
                      onClick={() => {
                        setConfirmDelete(null);
                        pz.deleteThemePreset(t.id);
                      }}
                    >
                      Delete “{t.name}”
                    </button>
                    <button type="button" className="vx-btn small" onClick={() => setConfirmDelete(null)}>
                      Keep
                    </button>
                  </>
                ) : (
                  <button type="button" className="vx-btn small danger" aria-label={`Delete ${t.name}`} onClick={() => setConfirmDelete(t.id)} data-testid="theme-preset-delete">
                    Delete
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
